import apiClient from './client';

export interface User {
  id: string;
  email: string;
  name: string;
  department?: string;
  clearance_level?: number;
  created_at: string;
  updated_at: string;
  preferences?: UserPreferences;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  language: string;
  notifications_enabled: boolean;
  email_notifications: boolean;
  dashboard_layout: any;
}

export interface CreateUserRequest {
  email: string;
  name: string;
  department?: string;
  clearance_level?: number;
}

export interface UpdateUserRequest {
  name?: string;
  department?: string;
  clearance_level?: number;
}

export interface UserSearchParams {
  page?: number;
  limit?: number;
  sort?: string;
  search?: string;
  department?: string;
  status?: 'active' | 'inactive';
}

export interface UserListResponse {
  users: User[];
  total_count: number;
  page_count: number;
}

/**
 * User API methods
 */
class UserApi {
  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<any>('/users/profile');
    // Map API response to frontend User interface
    return {
      id: response.id,
      email: response.email,
      name: response.full_name || response.username || 'Unknown User',
      department: response.department,
      clearance_level: response.clearance_level,
      created_at: response.created_at,
      updated_at: response.updated_at,
      preferences: response.preferences,
    };
  }

  /**
   * Update current user profile
   */
  async updateCurrentUser(data: UpdateUserRequest): Promise<User> {
    return apiClient.patch<User>('/users/profile', data);
  }

  /**
   * Get current user preferences
   */
  async getUserPreferences(): Promise<UserPreferences> {
    return apiClient.get<UserPreferences>('/users/preferences');
  }

  /**
   * Update current user preferences
   */
  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    return apiClient.patch<UserPreferences>('/users/preferences', preferences);
  }

  /**
   * Get user by ID (admin only)
   */
  async getUser(id: string): Promise<User> {
    const response = await apiClient.get<any>(`/users/${id}`);
    // Map API response to frontend User interface
    return {
      id: response.id,
      email: response.email,
      name: response.full_name || response.username || 'Unknown User',
      department: response.department,
      clearance_level: response.clearance_level,
      created_at: response.created_at,
      updated_at: response.updated_at,
      preferences: response.preferences,
    };
  }

  /**
   * List users with pagination and filtering (admin only)
   */
  async getUsers(params?: UserSearchParams): Promise<UserListResponse> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.department) searchParams.append('department', params.department);
    if (params?.status) searchParams.append('status', params.status);

    const query = searchParams.toString();
    const url = query ? `/users?${query}` : '/users';
    
    return apiClient.get<UserListResponse>(url);
  }

  /**
   * Create new user (admin only)
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    return apiClient.post<User>('/users', data);
  }

  /**
   * Update user (admin only)
   */
  async updateUser(id: string, data: UpdateUserRequest): Promise<User> {
    return apiClient.patch<User>(`/users/${id}`, data);
  }

  /**
   * Delete user (admin only)
   */
  async deleteUser(id: string): Promise<void> {
    return apiClient.delete<void>(`/users/${id}`);
  }

  /**
   * Get user activity/audit log (admin only)
   */
  async getUserActivity(userId: string, params?: {
    page?: number;
    limit?: number;
    from?: string;
    to?: string;
  }): Promise<any[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.limit) searchParams.append('limit', params.limit.toString());
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);

    const query = searchParams.toString();
    const url = query 
      ? `/users/${userId}/activity?${query}` 
      : `/users/${userId}/activity`;
    
    return apiClient.get<any[]>(url);
  }

  /**
   * Check if email is available during registration
   */
  async checkEmailAvailability(email: string): Promise<{ available: boolean }> {
    return apiClient.get<{ available: boolean }>(`/users/check-email?email=${encodeURIComponent(email)}`);
  }

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>('/users/password-reset', { email });
  }

  /**
   * Verify email address
   */
  async verifyEmail(token: string): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>('/users/verify-email', { token });
  }

  /**
   * Resend email verification
   */
  async resendEmailVerification(): Promise<{ success: boolean }> {
    return apiClient.post<{ success: boolean }>('/users/resend-verification');
  }

  /**
   * Get user departments (for dropdown lists)
   */
  async getDepartments(): Promise<string[]> {
    return apiClient.get<string[]>('/users/departments');
  }

  /**
   * Upload user avatar
   */
  async uploadAvatar(file: File, onUploadProgress?: (progress: number) => void): Promise<{ avatar_url: string }> {
    return apiClient.uploadFile<{ avatar_url: string }>(
      '/users/avatar', 
      file, 
      (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onUploadProgress(Math.round(progress));
        }
      }
    );
  }

  /**
   * Delete user avatar
   */
  async deleteAvatar(): Promise<void> {
    return apiClient.delete<void>('/users/avatar');
  }
}

// Export singleton instance
const userApi = new UserApi();
export default userApi;